import { useState } from 'react';
import { Campaign, CampaignChannels } from 'domains/campaigns/types';
import EmailPreviewContent from 'domains/content/components/EmailPreviewContent';
import SmsPreview from 'pages/ContentSmsBuilderPage/components/SmsPreview';
import useSenderProfile from 'hooks/queries/useSenderProfile';
import CampaignDetailsLayout from '../CampaignDetailsLayout';
import CurationListAndFirstAudience from '../CurationListAndFirstAudience';
import CustomCampaignDetails from './CustomCampaignDetails/CustomCampaignDetails';
import CustomCampaignDetailsNavigation from './CustomCampaignDetailsNavigation';
import GraphCampaignDetailsNavigation from './GraphCampaignDetailsNavigation';
import DetailItem from 'domains/core/components/DetailItem';
import useSmsData from 'pages/ContentSmsBuilderPage/hooks/useSmsData';
import useFormatSmsContent from 'pages/ContentSmsBuilderPage/hooks/useFormatSmsContent';
import VoicePreviewContent from 'domains/content/components/VoicePreviewContent';
import { getFormattedSmsAutoResponses } from 'pages/ContentSmsBuilderPage/components/SmsPreview/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useSmsSenderProfileById from 'hooks/queries/useSmsSenderProfileById';
import { getFormattedSmsSenderNumber } from 'domains/campaigns/utils';

type Props = {
    campaign: Campaign;
};

const CustomCampaignDetailsPage = ({ campaign }: Props) => {
    const [contentPreviewId, setContentPreviewId] = useState<number | null>();
    const closePreview = () => setContentPreviewId(null);

    const { smsSender: hasSmsSenderFlag, campaignGraphNavigation: hasCampaignGraphNavigationFlag } = useFlags();

    const previewContent = !!contentPreviewId && campaign.sends.find((send) => send.id === contentPreviewId);
    const isPreviewOpen = Boolean(previewContent);

    // Email-specific data
    const { data: previewContentSenderProfile } = useSenderProfile(previewContent?.emailSenderProfileId, {
        enabled: isPreviewOpen && campaign.channel === CampaignChannels.EMAIL,
    });

    // SMS-specific data
    const { mergeTags } = useSmsData({
        id: `${previewContent?.smsContentId}`,
        enabled: isPreviewOpen && campaign.channel === CampaignChannels.SMS,
    });

    const { data: smsSenderProfileData } = useSmsSenderProfileById(previewContent?.smsSenderProfileId, {
        enabled: hasSmsSenderFlag && isPreviewOpen && campaign.channel === CampaignChannels.SMS,
    });

    // Only parse contentSnapshot as JSON for SMS/VOICE channels
    // For EMAIL, contentSnapshot contains HTML
    const campaigncontentSnapshot = (() => {
        try {
            if (campaign.channel === CampaignChannels.SMS || campaign.channel === CampaignChannels.VOICE_CALL) {
                return JSON.parse(campaign?.sends[0]?.contentSnapshot || '{}');
            }
            return {};
        } catch (error) {
            return {};
        }
    })();

    const formattedPreviewContent = useFormatSmsContent(campaigncontentSnapshot?.content?.text, mergeTags);
    const formattedSmsAutoResponses = getFormattedSmsAutoResponses(campaigncontentSnapshot?.autoResponses, mergeTags);

    const reEntryDimension = campaign.reentryDetails?.dimension === 'hour' ? 'hours' : 'days';

    const renderPreviewContent = () => {
        if (!isPreviewOpen) return null;

        switch (campaign.channel) {
            case CampaignChannels.EMAIL:
                return (
                    <EmailPreviewContent
                        emailTemplateHTML={previewContent?.contentSnapshot}
                        fromAddress={previewContentSenderProfile?.from_address}
                        isGridItem
                        isOpen={isPreviewOpen}
                        preHeader={previewContent?.preHeader}
                        subjectLine={previewContent?.subjectLine}
                        onClose={closePreview}
                    />
                );
            case CampaignChannels.SMS:
                return (
                    <SmsPreview
                        formattedContent={formattedPreviewContent || ''}
                        autoResponses={formattedSmsAutoResponses}
                        senderNumber={getFormattedSmsSenderNumber(smsSenderProfileData)}
                    />
                );
            case CampaignChannels.VOICE_CALL:
                try {
                    const voiceJson = JSON.parse(previewContent?.contentSnapshot || '{}');
                    return (
                        <VoicePreviewContent
                            isOpen={!!contentPreviewId}
                            onClose={() => setContentPreviewId(null)}
                            voiceJson={voiceJson}
                        />
                    );
                } catch (error) {
                    return <></>;
                }
            default:
                return <></>;
        }
    };

    return (
        <CampaignDetailsLayout
            campaign={campaign}
            left={
                <>
                    <CurationListAndFirstAudience
                        campaign={campaign}
                        onChange={closePreview}
                        contentPreviewNodeId={contentPreviewId}
                        handlePreviewClick={setContentPreviewId}
                    />
                    <CustomCampaignDetails
                        campaign={campaign}
                        contentPreviewNodeId={contentPreviewId}
                        handlePreviewClick={setContentPreviewId}
                    />
                    {campaign?.reentryDetails && (
                        <DetailItem
                            header="Contacts Re-Enter"
                            description={`${campaign.reentryDetails.value} ${reEntryDimension} after last send`}
                        />
                    )}
                </>
            }
            right={
                <>
                    {renderPreviewContent()}
                    {hasCampaignGraphNavigationFlag ? (
                        <GraphCampaignDetailsNavigation campaign={campaign} isPreviewOpen={isPreviewOpen} />
                    ) : (
                        <CustomCampaignDetailsNavigation campaign={campaign} isPreviewOpen={isPreviewOpen} />
                    )}
                </>
            }
        />
    );
};

export default CustomCampaignDetailsPage;
